import * as _ from 'lodash';
import * as moment from 'moment';
import { Component } from 'react';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, RecipientActionCreators, AppActionCreators, IRecipientLabsReviewedResponse, IToolboxAvoidsRequest, IRecipientSaveToolboxAvoidsResponse } from '@nkr/store/index';
import { IBaseResponse, IDonor, IRecipient } from '@nkr/models/index';
import { Alert, Button, FormControl, Modal } from 'react-bootstrap';
import RecipientNav from '../../recipients/recipient-nav';
import Highcharts from 'highcharts';
import { toast } from "react-toastify";
import HighchartsReact from 'highcharts-react-official';
import RecipientPreferences from './preferences';
import RecipientDetails from './details';
import DirectedDonors from './directed-donors';
import RecipientAntibodies from './antibodies';
import RefreshWarning from './refresh-warning';
import ErrorMessageModal from './error-message-modal';
import ToolboxEpletGraph from './toolbox-eplet-graph';

interface IProps {
    recipientId: number,
    centerId: number,
    onUpdateRecipientAlias: (alias: string) => void
}

interface IStateProps {
    groupIsSuper: boolean
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getToolbox: (recipientId: number) => Promise<any>,
    saveToolboxPreferences: (recipientId: number, requireEplet: number, ebvPreference: number, cmvPreference: number, screeningComments: string) => Promise<IBaseResponse>,
    saveToolboxAvoids: (recipientId: number, data: IToolboxAvoidsRequest) => Promise<IRecipientSaveToolboxAvoidsResponse>,
    labsReviewed: (recipientId: number) => Promise<IRecipientLabsReviewedResponse>
}

type TProps = IStateProps & IDispatchProps & IProps;

interface IState {
    [index: string]: string | number | boolean | IRecipient | IDonor | any[]
    recipient: IRecipient,
    recipientSrc: IRecipient,
    directedDonors: IDonor[],
    labReviewHistory: string[],
    hasAlleleMatchCount: boolean,
    xAxis_Allele: number[];
    yAxis_Allele: number[],
    showWarning: boolean,
    labLastReviewedDate: string,
    labLastReviewedDatePending: string,
    antibodyPendingChanges: boolean,
    labLastReviewedDateError: string,
    errorMessage: string
}

class RecipientToolbox extends Component<TProps, IState> {
    readonly state: IState = {
        recipient: null,
        recipientSrc: null,
        directedDonors: [],
        labReviewHistory: [],
        hasAlleleMatchCount: false,
        xAxis_Allele: [],
        yAxis_Allele: [],
        showWarning: false,
        labLastReviewedDate: "",
        labLastReviewedDatePending: "",
        antibodyPendingChanges: false,
        labLastReviewedDateError: '',
        errorMessage: ''
    }

    componentDidMount = () => {
        this.getData();

        document.title = 'NKR - Recipient Toolbox';
    }

    handleInputChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        this.setState({
            [name]: value
        });
    }

    handleAntibodyInputChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let labLastReviewedDateError = this.state.labLastReviewedDateError;

        if (name === 'labLastReviewedDate' && value) {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            labLastReviewedDateError = new Date(value) > today ? 'Date cannot be in the future.' : '';
        }

        this.setState({
            [name]: value,
            labLastReviewedDateError,
            antibodyPendingChanges: this.hasPendingChanges(this.state.recipient, value)
        });
    }

    getData = () => {
        this.props.showLoading('Getting toolbox data, one moment please...').then(() => {
            this.props.getToolbox(this.props.recipientId).then(response => {
                if (response.data.success) {
                    let { recipient, directedDonors, labReviewHistory,
                        hasAlleleMatchCount, xAxis_Allele, yAxis_Allele } = response.data.results;

                    let labLastReviewedDate = moment(recipient.labLastReviewedDate).isValid() ? moment(recipient.labLastReviewedDate).format("YYYY-MM-DD") : "";

                    this.setState({
                        recipient,
                        recipientSrc: recipient,
                        directedDonors, labReviewHistory,
                        labLastReviewedDate,
                        hasAlleleMatchCount, xAxis_Allele, yAxis_Allele
                    });

                    document.title = 'NKR - Recipient Toolbox - ' + recipient.alias;

                    this.props.onUpdateRecipientAlias(recipient.alias);
                }
                else
                {
                    if(response.data.message)
                        toast.error(response.data.message);
                    else
                         toast.error('Error getting toolbox data.');
                }

                this.props.hideLoading();
            });
        });
    }

    handleReviewPotentialDonors = () => {
        window.location.href = '/center/' + this.props.centerId + '/recipient/' + this.props.recipientId + '/potential-donors';
    }

    hasPendingChanges = (recipient: IRecipient, reviewDate: string) => {
        return !_.isEqual(this.state.recipientSrc.hlaAvoids, recipient.hlaAvoids)
            || !moment(reviewDate).isSame(this.state.recipientSrc.labLastReviewedDate);
    }

    handleSavePreferences = () => {
        let { screeningComments, preferences } = this.state.recipient;

        this.props.showLoading('Saving preferences, one moment please...').then(() => {
            this.props.saveToolboxPreferences(this.props.recipientId, preferences.requireEplet, preferences.ebvPreference, preferences.cmvPreference, screeningComments)
                .then(response => {
                    if (response.data.success) {
                        this.setState({
                            showWarning: true
                        });
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                    toast.success('Preferences saved successfully');
                });
        });
    }

    handleSaveAvoids = () => {
        if (this.state.labLastReviewedDateError) return;

        let { aAvoids, bAvoids, drAvoids, bwAvoids, cwAvoids, dqAvoids: dqbAvoids, dqaAvoids, dpAvoids: dpbAvoids, avoidDr51, avoidDr52, avoidDr53 } = this.state.recipient.hlaAvoids;

        let data: IToolboxAvoidsRequest = {
            aAvoids,
            bAvoids,
            drAvoids,
            bwAvoids,
            cwAvoids,
            dqbAvoids,
            dqaAvoids,
            dpbAvoids,
            avoidDr51,
            avoidDr52,
            avoidDr53,
            labLastReviewedDate: this.state.labLastReviewedDate
        }

        this.props.showLoading('Saving avoids, one moment please...').then(() => {
            this.props.saveToolboxAvoids(this.props.recipientId, data)
                .then(response => {
                    if (response.data.success) {
                        let { recipient, labReviewHistory } = response.data.results;
                        let labLastReviewedDate = moment(recipient.labLastReviewedDate).isValid() ? moment(recipient.labLastReviewedDate).format("YYYY-MM-DD") : "";

                        this.setState({
                            recipient,
                            recipientSrc: recipient,
                            labLastReviewedDate,
                            labReviewHistory,
                            showWarning: true,
                            antibodyPendingChanges: this.hasPendingChanges(recipient, this.state.labLastReviewedDate)
                        });
                    } else {
                        if (response.data.message) {
                            this.setState({
                                errorMessage: response.data.message
                            });
                        } else {
                            this.setState({
                                errorMessage: 'Error saving the avoids'
                            });
                        }
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleAvoidToggle = (hla: string, antigen: number) => {
        let avoidName = hla.toLowerCase() + 'Avoids';
        let recipient = _.cloneDeep(this.state.recipient);

        for (let [key, value] of Object.entries(recipient.hlaAvoids)) {
            if (key == avoidName) {
                if (value.indexOf(antigen) >= 0) {
                    value = value.filter(a => a != antigen);
                } else {
                    value.push(antigen);
                }

                recipient.hlaAvoids[avoidName] = value;
            }
        }

        this.setState({
            recipient,
            antibodyPendingChanges: this.hasPendingChanges(recipient, this.state.labLastReviewedDate)
        });
    }

    handleAvoidToggleBoolean = (antigen: number) => {
        let avoidName = 'avoidDr' + antigen;
        let recipient = _.cloneDeep(this.state.recipient);

        for (let [key, value] of Object.entries(recipient.hlaAvoids)) {
            if (key == avoidName) {
                recipient.hlaAvoids[avoidName] = !value;
            }
        }

        this.setState({
            recipient,
            antibodyPendingChanges: this.hasPendingChanges(recipient, this.state.labLastReviewedDate)
        });
    }

    handleCloseWarning = () => {
        this.setState({
            showWarning: false
        });
    }

    handleUpdateLabReviewDate = () => {
        this.props.showLoading('Updating labs reviewed, one moment please...').then(() => {
            this.props.labsReviewed(this.props.recipientId).then(response => {
                if (response.data.success) {
                    let recipient = _.cloneDeep(this.state.recipient);
                    recipient.labLastReviewedDate = moment().format('YYYY-MM-DD');

                    this.setState({
                        recipient,
                        labReviewHistory: response.data.results
                    });
                }
            }).finally(() => {
                this.props.hideLoading();
            })
        });
    }

    handleChangePreferences = (name: string, value: any) => {
        let recipient = _.cloneDeep(this.state.recipient);

        if (name == 'requireEplet') {
            recipient.preferences.requireEplet = parseInt(value);
        }

        if (name == 'cmvPreference') {
            recipient.preferences.cmvPreference = parseInt(value);
        }

        if (name == 'ebvPreference') {
            recipient.preferences.ebvPreference = parseInt(value);
        }

        if (name == 'requirePreselect') {
            recipient.preferences.requirePreselect = false;
        }

        if (name == 'screeningComments') {
            recipient.screeningComments = value;
        }

        this.setState({
            recipient
        });
    }

    render = () => {
        const recipientAlias = this.state.recipient != null ? this.state.recipient.alias : '';

        var yAlleleLabels = ["0/8", "1/8", "2/8", "3/8", "4/8", "5/8", "6/8", "7/8", "8/8"];

        var yAlleleWait = [
            null,
            `A donor with a <b>1/8</b> match is expected to be found within the first {MONTH} month(s).`,
            `A donor with a <b>2/8</b> match is expected to be found within the first {MONTH} month(s).`,
            `A donor with a <b>3/8</b> match is expected to be found within the first {MONTH} month(s).`,
            `A donor with a <b>4/8</b> match is expected to be found within the first {MONTH} month(s).`,
            `A donor with a <b>5/8</b> match is expected to be found within the first {MONTH} month(s).`,
            `A donor with a <b>6/8</b> match is expected to be found within the first {MONTH} month(s).`,
            `A donor with a <b>7/8</b> match is expected to be found within the first {MONTH} month(s).`,
            `A donor with a <b>8/8</b> match is expected to be found within the first {MONTH} month(s).`
        ];

        let alleleOptions: Highcharts.Options = {
            chart: { type: 'spline' },
            title: {
                text: "<div><span><b style='font-size:22px;'>Time to Allele Match</b></span></div>",
                useHTML: true
            },
            xAxis: [{
                categories: this.state.xAxis_Allele.map(x => x.toString()),
                crosshair: true,
                title: { text: 'Months' }

            }],
            plotOptions: {
                spline: {
                    lineWidth: 4,
                    states: {
                        hover: {
                            lineWidth: 5
                        }
                    },
                    marker: {
                        enabled: false
                    }
                }
            },
            yAxis: [{
                min: 0,
                max: 8,
                tickPositions: [0, 1, 2, 3, 4, 5, 6, 7, 8],
                labels: {
                    formatter: function () {
                        return yAlleleLabels[this.value] || '';
                    },
                    style: {
                        color: '#434348'
                    }
                },
                title: {
                    text: 'Allele Match',
                    style: {
                        color: '#434348'
                    }
                }
            }],
            tooltip: {
                formatter: function () {
                    const messageTemplate = yAlleleWait[this.y] || '';
                    return messageTemplate.replace('{MONTH}', String(this.x));
                }
            },




            legend: {
                enabled: false
            },
            series: [{
                name: 'Allele Match',
                type: 'spline',
                data: this.state.xAxis_Allele.map((x, i) => ({
                    x: x,
                    y: this.state.yAxis_Allele[i]
                })),
                color: 'rgb(44, 175, 254)'
            }]

        };

        return (
            <>
                <div style={{ padding: 10 }}>
                    <RecipientNav
                        activeKey={'TOOLBOX'}
                        isAdmin={false}
                        isNkrLogin={this.props.groupIsSuper}
                        centerId={this.props.centerId}
                        recipientId={this.props.recipientId}
                        recipientAlias={recipientAlias}
                    />

                    <div style={{ borderWidth: '0px 1px 1px 1px', borderStyle: 'transparent', borderColor: '#ccc', borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }}>
                        {this.state.recipient != null && (
                            <div className='container' style={{ paddingTop: 20, paddingBottom: 20 }}>
                                {this.state.recipient.bloodType == '' && (
                                    <Alert bsStyle='danger' bsSize='sm'><strong>WARNING!</strong> The recipient's ABO is not specified...</Alert>
                                )}

                                <RecipientPreferences
                                    isChainEnd={this.state.recipient.isChainEnd}
                                    requireEplet={this.state.recipient.preferences.requireEplet}
                                    ebvPreference={this.state.recipient.preferences.ebvPreference}
                                    cmvPreference={this.state.recipient.preferences.cmvPreference}
                                    expectedWaitTimeLowEmm={this.state.recipient.expectedWaitTimeLowEmm}
                                    requirePreselect={0}
                                    screeningComments={this.state.recipient.screeningComments}
                                    histogeneticsRawTypingFile={this.state.recipient.histogeneticsRawTypingFile}
                                    onChange={this.handleChangePreferences}
                                    onReview={this.handleReviewPotentialDonors}
                                    onSave={this.handleSavePreferences} />

                                <ToolboxEpletGraph recipient={this.state.recipient} />


                                {this.state.hasAlleleMatchCount && (
                                    <div style={{ width: '100%', overflow: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: 5, marginBottom: 20 }}>
                                        <HighchartsReact
                                            highcharts={Highcharts}
                                            options={alleleOptions} />
                                    </div>
                                )}

                                {this.state.recipient.isChainEnd ?
                                    <RecipientDetails recipient={this.state.recipient} />
                                    :
                                    <DirectedDonors recipient={this.state.recipient} list={this.state.directedDonors} />
                                }

                                <RecipientAntibodies
                                    labLastReviewedDate={this.state.labLastReviewedDate}
                                    labLastReviewedDateError={this.state.labLastReviewedDateError}
                                    recipient={this.state.recipient}
                                    labReviewHistory={this.state.labReviewHistory}
                                    pendingChanges={this.state.antibodyPendingChanges}
                                    handleInputChange={this.handleAntibodyInputChange}
                                    onToggle={this.handleAvoidToggle}
                                    onToggleBoolean={this.handleAvoidToggleBoolean}
                                    handleSaveAvoids={this.handleSaveAvoids} />
                            </div>
                        )}
                    </div>
                </div>

                <RefreshWarning show={this.state.showWarning} onClose={this.handleCloseWarning} />

                <ErrorMessageModal
                    errorMessage={this.state.errorMessage}
                    onCancel={() => this.setState({ errorMessage: '' })}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getToolbox: (recipientId) => {
            return dispatch(RecipientActionCreators.getToolbox(recipientId));
        },
        saveToolboxPreferences: (recipientId, requireEplet, ebvPreference, cmvPreference, screeningComments) => {
            return dispatch(RecipientActionCreators.saveToolboxPreferences(recipientId, requireEplet, ebvPreference, cmvPreference, screeningComments));
        },
        saveToolboxAvoids: (recipientId, data) => {
            return dispatch(RecipientActionCreators.saveToolboxAvoids(recipientId, data));
        },
        labsReviewed: (recipientId) => {
            return dispatch(RecipientActionCreators.labsReviewed(recipientId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps, mapDispatchToProps)(RecipientToolbox)